import { trpc } from '@/main'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { reportQueryOptions, reportSearchDeps } from '@/hooks/use-report'
import { deriveProfiles } from '@/hooks/use-root-search'
import { noop } from '@tanstack/react-query'

/**
 * Layout for a selected team (`/teams/$teamId/*`). It renders real content, so per AGENTS.md it OWNS the
 * teamId-keyed chrome (getTeam + members list) all five tab children read. The chrome ABOVE this match
 * (teams.tsx's report pane, the members panel) can't be handed that context, so it reads THIS route's with
 * `useMatch({ from: '/_app/teams/$teamId' })`. The REPORT query is owned here too, as in the other three
 * sections: on the leaf it re-keys that match on every ámbito/source switch, and a leaf at `pendingMs: 0`
 * paints its skeleton the moment its match reloads — Evaluate/Evaluation warming it unused is the cheaper half.
 */
export const Route = createFileRoute('/_app/teams/$teamId')({
  // EXACTLY the search the three keys below are built from — nothing else may re-run this loader.
  loaderDeps: ({ search }) => ({ ...reportSearchDeps(search), o: search.o, od: search.od, q: search.q }),
  context: ({ deps, params }) => {
    // The team subviews are never the /report page, so isReportPage is false here; `deriveProfiles` is
    // what `useRootSearch` already applies at render, so both sides see the same `first`/`second`.
    const search = { ...deps, ...deriveProfiles(deps, params, false) }
    const teamId = Number(params.teamId)
    // The filters are whichever SELECTED profile is this team — the tab children and the chrome above
    // this match all read the field below, so this derivation exists once, here.
    const teamProfile =
      search.first?.type === 'team' && search.first.id === teamId
        ? search.first
        : search.second?.type === 'team' && search.second.id === teamId
          ? search.second
          : undefined
    return {
      teamQueryOptions: trpc.teams.getTeam.queryOptions({
        teamId,
        filters: {
          type: search.type,
          evaluation: teamProfile?.evaluation,
          members: teamProfile && 'members' in teamProfile ? teamProfile.members : undefined,
          sort: search.o,
          sortDirection: search.od,
        },
      }),
      // The members list as a visit lands on it. The panel that renders it sits in the parent
      // `teams.tsx`, above this match, so it calls the same factory with its live `q`.
      teamMembersInfiniteQueryOptions: trpc.teams.searchMembersInfinite.infiniteQueryOptions(
        { teamId: Number(params.teamId), q: deps.q, o: deps.o, od: deps.od, type: deps.type },
        { getNextPageParam: (lastPage) => lastPage.nextCursor },
      ),
      reportQueryOptions: reportQueryOptions(deps, params),
    }
  },
  loader: ({ context }) => {
    context.queryClient.query(context.teamQueryOptions).catch(noop)
    context.queryClient.infiniteQuery(context.teamMembersInfiniteQueryOptions).catch(noop)
    if (context.reportQueryOptions) context.queryClient.query(context.reportQueryOptions).catch(noop)
  },
  component: RouteComponent,
  // No pendingComponent: this passthrough layout just renders <AnimatedOutlet/>. The data-cold case is
  // covered by the parent teams.tsx (<Suspense fallback={ReportPanelSkeleton}>), and each tab leaf
  // (report/board/distribution) owns its own tab-specific skeleton — a generic one here would mismatch.
})

function RouteComponent() {
  return <Outlet />
}
